import { axiosInstance } from "../../utils/axios";

/**
 * A scorable V2 scoring model as returned by argo's frontend-facing catalog
 * (`GET /argo-api/v1/tenant/:tenant/scoring/catalog`). The catalog only lists
 * models that are ready to score (`status: live` + `parquet.status: done`), one
 * row per family (a family has at most one live version).
 *
 * Frontends key their score columns by `family` (stable across re-publish) and
 * send it downstream as the cs-api `model_scores[].model_id`. The heavy `model`
 * blob is never included in the catalog projection.
 */
export interface ScoringModel {
  /** nanoid, unique per model version. */
  _id: string;
  /** Stable model identity, shared by every version. This is the join key sent
   * to cs-api as `model_scores[].model_id`. */
  family: string;
  /** Human label for the score column. */
  name: string;
  /** Per-family version number of the live model. */
  version: number;
  /** The model's customer-facing one-liner; absent when it has none, or when
   * the catalog predates it. */
  description?: string;
}

/**
 * Fetches the tenant's scorable V2 scoring models (the score-column picker
 * source). Returns an empty array when the tenant has no live+exported models.
 *
 * @param tenantId - numeric mk-auth tenant id (from `getTenantId()`).
 */
export async function fetchScoringCatalog(
  tenantId: string
): Promise<ScoringModel[]> {
  const { data } = await axiosInstance.get<ScoringModel[]>(
    `/argo-api/v1/tenant/${tenantId}/scoring/catalog`
  );
  return data ?? [];
}

/** The full scoring model config (pillars → dimensions → sub-dimensions → rules,
 * with labels). Opaque here — forwarded verbatim as the `config` arg of the
 * `aiScoringScore` query; only the scoring engine interprets its structure. */
export type ScoringModelConfig = Record<string, unknown>;

/**
 * A single published model as returned by argo's get-by-id endpoint
 * (`GET /argo-api/v1/tenant/:tenant/scoring/models/:model_id`). Unlike the list
 * endpoint — which strips `model.pillars` to stay lean — this returns the full
 * document, so it carries the heavy `model` config blob intact.
 */
interface ScoringModelWithConfig extends ScoringModel {
  /** The full config blob (with labels) — sent to cs-api's `aiScoringScore`.
   * Argo stores it under `model`; `config` is tolerated as a fallback. */
  model?: ScoringModelConfig;
  config?: ScoringModelConfig;
}

function isObject(value: unknown): value is ScoringModelConfig {
  return typeof value === "object" && value !== null;
}

/**
 * Fetches the full config of a published model by its `_id`, for the on-demand
 * breakdown drill-down. The FE sends this config inline to cs-api's
 * `aiScoringScore` (cs-api is a stateless proxy and does not fetch from argo
 * itself), and the scoring engine requires a top-level `pillars`.
 *
 * MUST use the get-by-id endpoint, NOT the list: the list endpoint strips
 * `model.pillars` from every row (to keep the picker payload small), so a
 * config sourced from it would make the scoring engine 422 with
 * "Missing field: pillars". Returns null when the id has no published model.
 *
 * @param tenantId - numeric mk-auth tenant id (from `getTenantId()`).
 * @param modelId - the model's `_id` (from the catalog row / score column).
 */
export async function fetchScoringModelConfig(
  tenantId: string,
  modelId: string
): Promise<ScoringModelConfig | null> {
  const { data } = await axiosInstance.get<ScoringModelWithConfig>(
    `/argo-api/v1/tenant/${tenantId}/scoring/models/${modelId}`
  );
  if (!data) return null;
  // Argo stores the config blob under `model`; tolerate `config` as a fallback.
  if (isObject(data.model)) return data.model;
  if (isObject(data.config)) return data.config;
  return null;
}
